import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  CreditCard, ArrowLeft, Check, Crown, Globe,
  ShieldCheck, ArrowsLeftRight, Receipt, CalendarBlank,
  CaretRight, Wallet, X
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import BottomNav from '../../components/BottomNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChargeCard = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [cardStatus, setCardStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [statement, setStatement] = useState(null);
  const [cardType, setCardType] = useState('standard');
  const [billingAddress, setBillingAddress] = useState('');
  const [applying, setApplying] = useState(false);

  const cardColors = [
    { id: 'obsidian', name: 'Obsidian', bg: '#1C1C1C', text: '#fff' },
    { id: 'creme', name: 'Crème', bg: '#F5F0E6', text: '#1C1C1C' },
    { id: 'forest', name: 'Forêt', bg: '#145A3A', text: '#C3A365' },
    { id: 'gold', name: 'Or', bg: '#C3A365', text: '#1C1C1C', premium: true }
  ];

  useEffect(() => {
    fetchCardStatus();
  }, []);

  const fetchCardStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/charge-card/status`, { withCredentials: true });
      setCardStatus(data);
    } catch (error) {
      console.error('Failed to fetch card status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatement = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/charge-card/statement`, { withCredentials: true });
      setStatement(data);
      setShowStatement(true);
    } catch (error) {
      toast.error('Failed to load statement');
    }
  };

  const handleApply = async () => {
    if (!billingAddress.trim()) {
      toast.error(language === 'fr' ? 'Adresse requise' : 'Address required');
      return;
    }
    
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/charge-card/apply`,
        { card_type: cardType, billing_address: billingAddress, accept_terms: true },
        { withCredentials: true }
      );
      
      toast.success(data.message);
      setShowApply(false);
      fetchCardStatus();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Application failed');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1A14] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C3A365] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white pb-24">
      {/* Header */}
      <div className="px-5 pt-8 pb-4">
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-[#1E2F26] flex items-center justify-center"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          <div>
            <h1 className="text-2xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Charge Card
            </h1>
            <p className="text-[#8BA89A] text-sm">
              {language === 'fr' ? 'Carte de paiement propriétaire' : 'Proprietary payment card'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-5">
        {/* Has Card */}
        {cardStatus?.has_card ? (
          <div>
            {/* Card Visual */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <div 
                className="aspect-[1.6/1] rounded-2xl p-6 relative overflow-hidden"
                style={{ 
                  backgroundColor: cardColors.find(c => c.id === cardStatus.card.color)?.bg || '#1C1C1C',
                  boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
                }}
              >
                {/* Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
                    backgroundSize: '20px 20px'
                  }} />
                </div>

                {/* Content */}
                <div className="relative h-full flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <span className="brand-logo text-xl" style={{ 
                      color: cardColors.find(c => c.id === cardStatus.card.color)?.text || '#fff',
                      opacity: 0.8
                    }}>habitué</span>
                    {cardStatus.card.type === 'gold' && (
                      <Crown size={24} className="text-[#C3A365]" weight="fill" />
                    )}
                  </div>

                  <div>
                    <p className="text-sm opacity-50 mb-1" style={{ 
                      color: cardColors.find(c => c.id === cardStatus.card.color)?.text 
                    }}>
                      •••• •••• •••• {cardStatus.card.card_number_last4}
                    </p>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-xs opacity-50" style={{ 
                          color: cardColors.find(c => c.id === cardStatus.card.color)?.text 
                        }}>
                          {language === 'fr' ? 'Solde actuel' : 'Current Balance'}
                        </p>
                        <p className="text-2xl font-light" style={{ 
                          color: cardColors.find(c => c.id === cardStatus.card.color)?.text 
                        }}>
                          £{cardStatus.card.current_balance.toFixed(2)}
                        </p>
                      </div>
                      <p className="text-xs opacity-50 uppercase" style={{ 
                        color: cardColors.find(c => c.id === cardStatus.card.color)?.text 
                      }}>
                        {cardStatus.card.type}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Gold accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C3A365] to-transparent" />
              </div>
            </motion.div>

            {/* Card Info */}
            <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl divide-y divide-[#283D32] mb-6">
              <div className="flex justify-between items-center p-4">
                <span className="text-[#8BA89A]">{language === 'fr' ? 'Statut' : 'Status'}</span>
                <span className="text-green-400 flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-400" />
                  {language === 'fr' ? 'Actif' : 'Active'}
                </span>
              </div>
              <div className="flex justify-between items-center p-4">
                <span className="text-[#8BA89A]">{language === 'fr' ? 'Frais annuels' : 'Annual Fee'}</span>
                <span className="text-white">£{cardStatus.card.annual_fee}</span>
              </div>
              <div className="flex justify-between items-center p-4">
                <span className="text-[#8BA89A]">{language === 'fr' ? 'Échéance' : 'Due Date'}</span>
                <span className="text-white">{cardStatus.card.due_date?.slice(0, 10) || '-'}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={fetchStatement}
                className="w-full flex items-center justify-between p-4 bg-[#1E2F26] border border-[#283D32] rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <Receipt size={20} className="text-[#C3A365]" />
                  <span className="text-white">{language === 'fr' ? 'Voir le relevé' : 'View Statement'}</span>
                </div>
                <CaretRight size={18} className="text-[#8BA89A]" />
              </button>

              <button
                onClick={() => navigate('/topup')}
                className="w-full flex items-center justify-between p-4 bg-[#1E2F26] border border-[#283D32] rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <Wallet size={20} className="text-[#C3A365]" />
                  <span className="text-white">{language === 'fr' ? 'Payer le solde' : 'Pay Balance'}</span>
                </div>
                <CaretRight size={18} className="text-[#8BA89A]" />
              </button>
            </div>
          </div>
        ) : (
          /* No Card - Show Apply Option */
          <div>
            {/* Info Banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-[#145A3A]/30 to-[#0F1A14] border border-[#283D32] rounded-2xl p-6 mb-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <CreditCard size={28} className="text-[#C3A365]" />
                <h2 className="text-xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  habitué Charge Card
                </h2>
              </div>
              <p className="text-[#8BA89A] text-sm leading-relaxed mb-6">
                {language === 'fr' 
                  ? 'Carte de paiement propriétaire. Solde intégral payable chaque mois. Pas de crédit revolving, pas de taux d\'intérêt.'
                  : 'Proprietary payment card. Full balance due monthly. No revolving credit, no interest rates.'}
              </p>
              
              {/* Features */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-[#C3A365]/20 flex items-center justify-center mx-auto mb-2">
                    <Globe size={18} className="text-[#C3A365]" />
                  </div>
                  <p className="text-white text-xs">{language === 'fr' ? 'Réseau mondial' : 'Global Network'}</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-[#C3A365]/20 flex items-center justify-center mx-auto mb-2">
                    <ShieldCheck size={18} className="text-[#C3A365]" />
                  </div>
                  <p className="text-white text-xs">{language === 'fr' ? 'Pas de dette' : 'No Debt'}</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-[#C3A365]/20 flex items-center justify-center mx-auto mb-2">
                    <ArrowsLeftRight size={18} className="text-[#C3A365]" />
                  </div>
                  <p className="text-white text-xs">{language === 'fr' ? 'FX 1.5%' : 'FX 1.5%'}</p>
                </div>
              </div>
            </motion.div>

            {/* Card Tiers */}
            <div className="space-y-4 mb-8">
              <h3 className="text-white font-medium">{language === 'fr' ? 'Choisir votre carte' : 'Choose your card'}</h3>
              
              {/* Standard */}
              <motion.button
                onClick={() => { setCardType('standard'); setShowApply(true); }}
                className="w-full p-4 bg-[#1E2F26] border border-[#283D32] rounded-xl text-left hover:border-[#C3A365]/50 transition-colors"
                whileHover={{ scale: 1.01 }}
                data-testid="card-standard"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-10 rounded-lg bg-gradient-to-br from-[#2a2a2a] to-[#1a1a1a] flex items-end p-2">
                    <span className="text-[8px] text-white/50">habitué</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">Standard</p>
                    <p className="text-[#8BA89A] text-sm">£120/{language === 'fr' ? 'an' : 'year'}</p>
                  </div>
                  <CaretRight size={18} className="text-[#8BA89A]" />
                </div>
              </motion.button>

              {/* Gold */}
              <motion.button
                onClick={() => { setCardType('gold'); setShowApply(true); }}
                className="w-full p-4 bg-gradient-to-br from-[#C3A365]/20 to-transparent border border-[#C3A365]/30 rounded-xl text-left hover:border-[#C3A365] transition-colors relative overflow-hidden"
                whileHover={{ scale: 1.01 }}
                data-testid="card-gold"
              >
                <div className="absolute top-2 right-2 px-2 py-0.5 bg-[#C3A365] text-[#0F1A14] text-[10px] font-bold rounded">
                  {language === 'fr' ? 'MÉTAL' : 'METAL'}
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-10 rounded-lg bg-gradient-to-br from-[#C3A365] to-[#A08050] flex items-end p-2">
                    <span className="text-[8px] text-[#0F1A14]/50">habitué</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-[#C3A365] font-medium flex items-center gap-1">
                      <Crown size={14} weight="fill" />
                      Gold
                    </p>
                    <p className="text-[#8BA89A] text-sm">£250/{language === 'fr' ? 'an' : 'year'}</p>
                  </div>
                  <CaretRight size={18} className="text-[#C3A365]" />
                </div>
              </motion.button>
            </div>

            <p className="text-center text-[#8BA89A] text-xs">
              {language === 'fr' 
                ? 'Frais d\'acceptance marchand : 1.5% – 2.0%'
                : 'Merchant acceptance fees: 1.5% – 2.0%'}
            </p>
          </div>
        )}
      </div>

      {/* Apply Modal */}
      <AnimatePresence>
        {showApply && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
            onClick={() => setShowApply(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#18261E] rounded-t-3xl sm:rounded-3xl p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {language === 'fr' ? 'Demander une carte' : 'Apply for Card'}
                </h2>
                <button onClick={() => setShowApply(false)} className="text-[#8BA89A]">
                  <X size={24} />
                </button>
              </div>

              {/* Card Preview */}
              <div 
                className="aspect-[1.8/1] rounded-xl p-4 mb-6 flex flex-col justify-between"
                style={{ 
                  backgroundColor: cardType === 'gold' ? '#C3A365' : '#1C1C1C',
                  color: cardType === 'gold' ? '#0F1A14' : '#fff'
                }}
              >
                <div className="flex justify-between">
                  <span className="brand-logo text-lg opacity-80">habitué</span>
                  {cardType === 'gold' && <Crown size={20} weight="fill" />}
                </div>
                <div>
                  <p className="text-xs opacity-50">CHARGE CARD</p>
                  <p className="font-medium uppercase">{cardType}</p>
                </div>
              </div>

              {/* Form */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-[#8BA89A] text-sm mb-2">
                    {language === 'fr' ? 'Adresse de facturation' : 'Billing Address'}
                  </label>
                  <textarea
                    value={billingAddress}
                    onChange={(e) => setBillingAddress(e.target.value)}
                    className="w-full bg-[#1E2F26] border border-[#283D32] rounded-xl px-4 py-3 text-white placeholder:text-[#8BA89A]/50 focus:outline-none focus:border-[#C3A365]/50 resize-none"
                    rows={3}
                    placeholder="123 Rue Example, Paris 75001"
                    data-testid="billing-address"
                  />
                </div>

                <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-[#8BA89A]">{language === 'fr' ? 'Type de carte' : 'Card Type'}</span>
                    <span className="text-white capitalize">{cardType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8BA89A]">{language === 'fr' ? 'Frais annuels' : 'Annual Fee'}</span>
                    <span className="text-[#C3A365]">£{cardType === 'gold' ? '250' : '120'}</span>
                  </div>
                </div>

                <p className="text-[#8BA89A] text-xs">
                  {language === 'fr' 
                    ? 'En continuant, vous acceptez les conditions d\'utilisation de la Charge Card habitué.'
                    : 'By continuing, you agree to the habitué Charge Card terms of use.'}
                </p>
              </div>

              <button
                onClick={handleApply}
                disabled={applying || !billingAddress.trim()}
                className="w-full py-4 bg-[#C3A365] text-[#0F1A14] font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-apply"
              >
                {applying ? (
                  <div className="w-5 h-5 border-2 border-[#0F1A14] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Check size={20} weight="bold" />
                    {language === 'fr' ? 'Soumettre la demande' : 'Submit Application'}
                  </>
                )}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Statement Modal */}
      <AnimatePresence>
        {showStatement && statement && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
            onClick={() => setShowStatement(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#18261E] rounded-t-3xl sm:rounded-3xl p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {language === 'fr' ? 'Relevé mensuel' : 'Monthly Statement'}
                  </h2>
                  <p className="text-[#8BA89A] text-xs">
                    {statement.statement_period?.start?.slice(0, 10)} - {statement.statement_period?.end?.slice(0, 10)}
                  </p>
                </div>
                <button onClick={() => setShowStatement(false)} className="text-[#8BA89A]">
                  <X size={24} />
                </button>
              </div>

              {/* Summary */}
              <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-[#8BA89A] text-xs">{language === 'fr' ? 'Total dépenses' : 'Total Charges'}</p>
                    <p className="text-white text-xl">£{statement.total_charges?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[#8BA89A] text-xs">{language === 'fr' ? 'Paiements' : 'Payments'}</p>
                    <p className="text-green-400 text-xl">£{statement.total_payments?.toFixed(2)}</p>
                  </div>
                </div>
                <div className="border-t border-[#283D32] pt-4">
                  <div className="flex justify-between">
                    <span className="text-[#8BA89A]">{language === 'fr' ? 'Solde dû' : 'Balance Due'}</span>
                    <span className="text-[#C3A365] text-xl font-medium">£{statement.current_balance?.toFixed(2)}</span>
                  </div>
                  <p className="text-[#8BA89A] text-xs mt-1">
                    {language === 'fr' ? 'Échéance:' : 'Due:'} {statement.due_date?.slice(0, 10)}
                  </p>
                </div>
              </div>

              {/* Transactions */}
              <h3 className="text-white font-medium mb-3">{language === 'fr' ? 'Transactions' : 'Transactions'}</h3>
              <div className="space-y-2">
                {statement.transactions?.length > 0 ? (
                  statement.transactions.map((tx, i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-[#1E2F26] border border-[#283D32] rounded-lg">
                      <div>
                        <p className="text-white text-sm">{tx.type.replace(/_/g, ' ')}</p>
                        <p className="text-[#8BA89A] text-xs">{tx.created_at?.slice(0, 10)}</p>
                      </div>
                      <span className={tx.type.includes('payment') ? 'text-green-400' : 'text-white'}>
                        {tx.type.includes('payment') ? '+' : '-'}£{tx.amount?.toFixed(2)}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-[#8BA89A] py-4">
                    {language === 'fr' ? 'Aucune transaction' : 'No transactions'}
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
};

export default ChargeCard;
